import { X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { CrewFilters, PublishedMasterclassPickerItem } from "@/types/crew"

/**
 * OKS-25 F7 — Active-filter chip rail.
 *
 * Renders one chip per active filter parameter beneath the filter
 * bar. Each chip surfaces the param's display label (e.g.
 * "Location: Paris, FR") plus an X button that removes JUST that
 * filter (preserves the others). When ANY filter is active, a
 * "Clear all" ghost link surfaces at the trailing end.
 *
 * Locked testids:
 *   - `crew-list-active-filters` — chip rail wrapper (always rendered;
 *     `data-active="true|false"` for QA assertions)
 *   - `crew-list-active-filter-chip-{key}` — per-chip dismiss target.
 *     `key` ∈ `q` | `location` | `followers` | `tags-{tag}` | `joined`
 *   - `crew-list-filter-clear` — "Clear all" button (also surfaced
 *     in the filter rail toolbar by F6; rendered here as the trailing
 *     element of the chip rail when ANY chip is active)
 */
interface CrewListActiveFilterChipsProps {
  filters: CrewFilters
  /**
   * Drop a single filter param. For array-valued filters (`tags`),
   * `value` identifies the specific entry to remove; for the
   * paired `followers_*` and `joined_*` ranges, the chip removes
   * BOTH min/max in one call (handler responsible for emitting the
   * paired URL update).
   */
  onRemoveFilter: <K extends keyof CrewFilters>(key: K, value?: string) => void
  onClearAll: () => void
  /**
   * OKS-58 — Picker payload from `filter_options.published_masterclasses`.
   * Used ONLY to resolve the title for the masterclass-completion chip
   * label. When the active rule's id isn't in the list (stale unpublish
   * mid-session), the chip falls back to a generic localised "Masterclass"
   * + `(Unpublished)` suffix.
   */
  publishedMasterclasses: PublishedMasterclassPickerItem[]
  className?: string
}

export function CrewListActiveFilterChips({
  filters,
  onRemoveFilter,
  onClearAll,
  publishedMasterclasses,
  className,
}: CrewListActiveFilterChipsProps) {
  const { t } = useTranslation()

  const chips: {
    testIdSuffix: string
    label: string
    onRemove: () => void
  }[] = []

  if (filters.q && filters.q.trim() !== "") {
    chips.push({
      testIdSuffix: "q",
      label: `${t("crew.list.search_placeholder")}: "${filters.q}"`,
      onRemove: () => onRemoveFilter("q"),
    })
  }
  // OKS-348 — Single composite-string location chip. The
  // canonical_location_id branch is gone with the substrate.
  if (filters.location && filters.location.trim() !== "") {
    chips.push({
      testIdSuffix: "location",
      label: `${t("crew.filters.location")}: ${filters.location}`,
      onRemove: () => onRemoveFilter("location"),
    })
  }
  if (
    typeof filters.followers_min === "number" ||
    typeof filters.followers_max === "number"
  ) {
    const min = filters.followers_min ?? "—"
    const max = filters.followers_max ?? "—"
    chips.push({
      testIdSuffix: "followers",
      label: `${t("crew.filters.followers")}: ${min} – ${max}`,
      onRemove: () => onRemoveFilter("followers_min"),
    })
  }
  for (const tag of filters.tags) {
    chips.push({
      testIdSuffix: `tags-${tag}`,
      label: `#${tag}`,
      onRemove: () => onRemoveFilter("tags", tag),
    })
  }
  if (filters.joined_from || filters.joined_to) {
    const from = filters.joined_from ?? "…"
    const to = filters.joined_to ?? "…"
    chips.push({
      testIdSuffix: "joined",
      label: `${t("crew.filters.joined")}: ${from} – ${to}`,
      onRemove: () => onRemoveFilter("joined_from"),
    })
  }
  // OKS-27 — At-risk lifecycle filter chip. Boolean filter — chip
  // surfaces only when active; remove dismisses by setting `at_risk`
  // back to false (drops from URL via the `?at_risk=1`-truthy
  // emission rule in `buildCrewListParams`).
  if (filters.at_risk) {
    chips.push({
      testIdSuffix: "at-risk",
      label: t("crew.filter.at_risk"),
      onRemove: () => onRemoveFilter("at_risk"),
    })
  }

  // OKS-58 — Masterclass-completion rule chip. Both URL keys travel as
  // a pair; the chip renders only when both are active. Title resolved
  // from the picker payload; falls back to a localised
  // `(Unpublished)` suffix when the id isn't in the published list
  // (stale unpublish mid-session OR saved segment with a now-draft
  // target applied here via `?segment=<id>` — but segment-applied
  // raw chips are suppressed at the page level so in practice this
  // chip only fires for the URL-only path; the safety net stays).
  // Remove dismisses by clearing the `_op` key — the handler in
  // `CrewIndex` resets both keys.
  if (
    filters.masterclass_completion_op &&
    typeof filters.masterclass_completion_id === "number"
  ) {
    const op = filters.masterclass_completion_op
    const found = publishedMasterclasses.find(
      (m) => m.id === filters.masterclass_completion_id,
    )
    const baseLabel = found
      ? t(`crew_segments.filter.masterclass_completion.chip.${op}`, {
          title: found.title,
        })
      : `${t(`crew_segments.filter.masterclass_completion.operator.${op}`)} · ${t("crew_segments.filter.masterclass_completion.chip.suffix_unpublished")}`
    chips.push({
      testIdSuffix: "masterclass-completion",
      label: baseLabel,
      onRemove: () => onRemoveFilter("masterclass_completion_op"),
    })
  }

  const isActive = chips.length > 0

  return (
    <div
      data-testid="crew-list-active-filters"
      data-active={isActive ? "true" : "false"}
      className={cn(
        "flex flex-wrap items-center gap-2",
        !isActive && "hidden",
        className,
      )}
    >
      {chips.map((chip) => (
        <Badge
          key={chip.testIdSuffix}
          variant="secondary"
          className="gap-1 pr-1"
        >
          <span>{chip.label}</span>
          <button
            type="button"
            data-testid={`crew-list-active-filter-chip-${chip.testIdSuffix}`}
            // OKS-175 — Localized SR announcement. Single canonical key
            // (`crew.filters.chip_remove_aria`) with `%{filter}` interpolated
            // to the chip's visible label. Matches missions sibling convention.
            aria-label={t("crew.filters.chip_remove_aria", {
              filter: chip.label,
            })}
            onClick={chip.onRemove}
            className="hover:bg-muted/50 focus-visible:ring-ring rounded-full p-0.5 focus-visible:ring-2"
          >
            <X className="size-3" aria-hidden="true" />
          </button>
        </Badge>
      ))}
      {isActive && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="crew-list-filter-clear"
          onClick={onClearAll}
        >
          {t("crew.filters.clear")}
        </Button>
      )}
    </div>
  )
}
